import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Package, Mail, Truck, Save, User, Building, Phone, Mail as MailIcon, Bell } from 'lucide-react'
import { deliveryApi } from '../services/api'
import api from '../services/api'
import { showError } from '../utils/showError'
import PageHeader from '../components/common/PageHeader'
import LoadingSpinner from '../components/common/LoadingSpinner'

const EditDelivery = () => {
  const { deliveryId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [delivery, setDelivery] = useState(null)
  const [buildings, setBuildings] = useState([])
  const [users, setUsers] = useState([])
  const [buildingUnits, setBuildingUnits] = useState([])
  const [searchingUsers, setSearchingUsers] = useState(false)
  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [errors, setErrors] = useState({})
  
  const [formData, setFormData] = useState({
    deliveryId: '',
    recipientName: '',
    recipientAddress: '',
    recipientUnitNumber: '',
    recipientFloorNumber: '',
    recipientPhone: '',
    recipientEmail: '',
    senderAddress: '',
    senderName: '',
    serialNumber: '',
    deliveryType: 1,
    notificationMethods: [],
    isDelivered: false,
    deliveryDate: '',
    isCollected: false,
    collectedDate: '',
    recipientUserId: '',
    buildingUnitId: '',
    forceUpdate: false,
    skipValidation: false,
    notifyRecipient: true,
    adminNotes: '',
    updateReason: ''
  })

  useEffect(() => {
    loadDeliveryDetails()
    loadBuildings()
  }, [deliveryId])

  useEffect(() => {
    if (formData.buildingId) {
      loadBuildingUnits(formData.buildingId)
    }
  }, [formData.buildingId])

  useEffect(() => {
    if (userSearchTerm.length >= 2) {
      searchUsers(userSearchTerm)
    } else {
      setUsers([])
    }
  }, [userSearchTerm])

  const loadDeliveryDetails = async () => {
    try {
      setLoading(true)
      const response = await deliveryApi.get(deliveryId)
      const deliveryData = response.data

      console.log('Loaded delivery for editing:', deliveryData)

      // Convert delivery data to form format
      setFormData({
        deliveryId: deliveryData.deliveryId,
        recipientName: deliveryData.recipientName || '',
        recipientAddress: deliveryData.recipientAddress || '',
        recipientUnitNumber: deliveryData.recipientUnitNumber || '',
        recipientFloorNumber: deliveryData.recipientFloorNumber || '',
        recipientPhone: deliveryData.recipientPhone || '',
        recipientEmail: deliveryData.recipientEmail || '',
        senderAddress: deliveryData.senderAddress || '',
        senderName: deliveryData.senderName || '',
        serialNumber: deliveryData.serialNumber || '',
        deliveryType: deliveryData.deliveryType || 1,
        notificationMethods: deliveryData.notificationMethods || [],
        isDelivered: deliveryData.isDelivered || false,
        deliveryDate: deliveryData.deliveryDate ? new Date(deliveryData.deliveryDate).toISOString().slice(0, 16) : '',
        isCollected: deliveryData.isCollected || false,
        collectedDate: deliveryData.collectedDate ? new Date(deliveryData.collectedDate).toISOString().slice(0, 16) : '',
        recipientUserId: deliveryData.recipientUserId || '',
        buildingUnitId: deliveryData.buildingUnitId || '',
        buildingId: deliveryData.buildingId || '',
        forceUpdate: false,
        skipValidation: false,
        updateAnnouncement: false,
        notifyRecipient: true,
        customAnnouncementTitle: '',
        customAnnouncementContent: '',
        announcementPriority: 2,
        requireAcknowledgment: false,
        adminNotes: '',
        updateReason: ''
      })

      setDelivery(deliveryData)
      
      // Set the user search term if we have recipient info
      if (deliveryData.recipientName) {
        setUserSearchTerm(deliveryData.recipientName)
      }
    } catch (error) {
      console.error('Error loading delivery details:', error)
      alert('Failed to load delivery details.')
      navigate('/deliveries')
    } finally {
      setLoading(false)
    }
  }

  const loadBuildings = async () => {
    try {
      const response = await api.get('/Building', { params: { pageSize: 1000 } })
      setBuildings(response.data.items || [])
    } catch (error) {
      console.error('Error loading buildings:', error)
    }
  }

  const loadBuildingUnits = async (buildingId) => {
    try {
      const response = await api.get(`/Building/${buildingId}/units`)
      setBuildingUnits(response.data.items || [])
    } catch (error) {
      console.error('Error loading building units:', error)
      setBuildingUnits([])
    }
  }

  const searchUsers = async (searchTerm) => {
    try {
      setSearchingUsers(true)
      const response = await api.get('/Users', { 
        params: { 
          searchString: searchTerm,
          pageSize: 20
        }
      })
      setUsers(response.data.items || [])
    } catch (error) {
      console.error('Error searching users:', error)
      setUsers([])
    } finally {
      setSearchingUsers(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }

    // Auto-populate recipient details when user is selected
    if (field === 'recipientUserId' && value) {
      const selectedUser = users.find(u => u.userId === value)
      if (selectedUser) {
        setFormData(prev => ({
          ...prev,
          recipientName: `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim(),
          recipientEmail: selectedUser.email || '',
          recipientPhone: selectedUser.phoneNumber || ''
        }))
      }
    }
  }

  const handleNotificationMethodChange = (method, isChecked) => {
    setFormData(prev => ({
      ...prev,
      notificationMethods: isChecked
        ? [...prev.notificationMethods, method]
        : prev.notificationMethods.filter(m => m !== method)
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    // Required fields
    if (!formData.deliveryType) {
      newErrors.deliveryType = 'Delivery type is required'
    }

    // Conditional validations
    if (formData.isDelivered && !formData.deliveryDate) {
      newErrors.deliveryDate = 'Delivery date is required when marked as delivered'
    }
    if (formData.isCollected && !formData.collectedDate) {
      newErrors.collectedDate = 'Collection date is required when marked as collected'
    }
    if (formData.isCollected && !formData.isDelivered) {
      newErrors.isDelivered = 'Cannot be collected without being delivered first'
    }

    // Email validation
    if (formData.recipientEmail && !/\S+@\S+\.\S+/.test(formData.recipientEmail)) {
      newErrors.recipientEmail = 'Invalid email format'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setSaving(true)
      
      const submitData = {
        ...formData,
        // Convert string IDs to appropriate types
        deliveryId: parseInt(formData.deliveryId),
        buildingUnitId: formData.buildingUnitId ? parseInt(formData.buildingUnitId) : undefined,
        
        // Ensure dates are in proper format or undefined
        deliveryDate: formData.deliveryDate || undefined,
        collectedDate: formData.collectedDate || undefined,
      }

      // Remove empty strings and convert to proper types
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          delete submitData[key]
        }
      })

      console.log('Submitting delivery update:', submitData)
      
      const response = await deliveryApi.update(deliveryId, submitData)
      console.log('Delivery updated successfully:', response.data)
      
      navigate(`/deliveries/${deliveryId}`)
    } catch (error) {
      console.error('Error updating delivery:', error)
      showError(error)

      // Handle API validation errors
      if (error.response?.data?.errors) {
        const apiErrors = {}
        Object.keys(error.response.data.errors).forEach(key => {
          apiErrors[key.toLowerCase()] = error.response.data.errors[key][0]
        })
        setErrors(apiErrors)
      }
    } finally {
      setSaving(false)
    }
  }

  const getDeliveryTypeIcon = (type) => {
    switch (type) {
      case 1: return <Package className="w-5 h-5" />
      case 2: return <Mail className="w-5 h-5" />
      default: return <Truck className="w-5 h-5" />
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (!delivery) {
    return (
      <div className="text-center py-12">
        <Package className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Delivery not found</h3>
        <p className="mt-1 text-sm text-gray-500">The delivery you're trying to edit doesn't exist.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <div className="flex items-center">
            <button
              onClick={() => navigate(`/deliveries/${deliveryId}`)}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            Edit Delivery #{delivery.deliveryId}
          </div>
        }
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Delivery Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Type *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="deliveryType"
                      value={1}
                      checked={formData.deliveryType === 1}
                      onChange={(e) => handleInputChange('deliveryType', parseInt(e.target.value))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <Package className="ml-2 mr-1 w-4 h-4" />
                    <span className="text-sm text-gray-700">Package</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="deliveryType"
                      value={2}
                      checked={formData.deliveryType === 2}
                      onChange={(e) => handleInputChange('deliveryType', parseInt(e.target.value))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <Mail className="ml-2 mr-1 w-4 h-4" />
                    <span className="text-sm text-gray-700">Mail</span>
                  </label>
                </div>
                {errors.deliveryType && (
                  <p className="mt-1 text-sm text-red-600">{errors.deliveryType}</p>
                )}
              </div>

              {/* Building (Read-only display) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Building
                </label>
                <div className="bg-gray-100 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700">
                  {delivery.buildingName || 'Unknown Building'}
                  <div className="text-xs text-gray-500">{delivery.buildingAddress}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sender Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sender Name
                </label>
                <input
                  type="text"
                  value={formData.senderName}
                  onChange={(e) => handleInputChange('senderName', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Amazon, FedEx, etc."
                />
              </div>

              {/* Serial Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Serial/Tracking Number
                </label>
                <input
                  type="text"
                  value={formData.serialNumber}
                  onChange={(e) => handleInputChange('serialNumber', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Tracking or reference number"
                />
              </div>
            </div>

            {/* Sender Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sender Address
              </label>
              <textarea
                value={formData.senderAddress}
                onChange={(e) => handleInputChange('senderAddress', e.target.value)}
                rows={2}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="Sender's address"
              />
            </div>
          </div>
        </div>

        {/* Recipient Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recipient Information</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            {/* Current Recipient Display */}
            {delivery.recipientProfile && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Current Recipient</h4>
                <div className="flex items-center">
                  {delivery.recipientProfile.profilePictureUrl ? (
                    <img
                      src={delivery.recipientProfile.profilePictureUrl}
                      alt={delivery.recipientName}
                      className="w-10 h-10 rounded-full mr-3"
                    />
                  ) : (
                    <User className="w-10 h-10 p-2 bg-gray-200 rounded-full mr-3" />
                  )}
                  <div>
                    <div className="text-sm font-medium text-blue-900">
                      {delivery.recipientName}
                    </div>
                    <div className="text-sm text-blue-700">{delivery.recipientEmail}</div>
                  </div>
                </div>
              </div>
            )}

            {/* User Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Change Recipient (Optional)
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Search by name or email..."
                />
                {searchingUsers && (
                  <div className="absolute right-3 top-2">
                    <LoadingSpinner size="sm" />
                  </div>
                )}
              </div>
              
              {/* User Dropdown */}
              {users.length > 0 && (
                <div className="mt-2 max-h-60 overflow-y-auto border border-gray-300 rounded-md bg-white shadow-lg">
                  {users.map(user => (
                    <button
                      key={user.userId}
                      type="button"
                      onClick={() => {
                        handleInputChange('recipientUserId', user.userId)
                        setUserSearchTerm(`${user.firstName} ${user.lastName}`)
                        setUsers([])
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-200 last:border-b-0"
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recipient Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Name
                </label>
                <input
                  type="text"
                  value={formData.recipientName}
                  onChange={(e) => handleInputChange('recipientName', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              {/* Building Unit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Building Unit
                </label>
                <select
                  value={formData.buildingUnitId}
                  onChange={(e) => handleInputChange('buildingUnitId', e.target.value)}
                  disabled={!formData.buildingId}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                >
                  <option value="">Select a unit (optional)</option>
                  {buildingUnits.map(unit => (
                    <option key={unit.buildingUnitId} value={unit.buildingUnitId}>
                      {unit.unitNumber} - {unit.unitType}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Unit Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit Number
                </label>
                <input
                  type="text"
                  value={formData.recipientUnitNumber}
                  onChange={(e) => handleInputChange('recipientUnitNumber', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="101, 2A, etc."
                />
              </div>

              {/* Floor Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Floor Number
                </label>
                <input
                  type="text"
                  value={formData.recipientFloorNumber}
                  onChange={(e) => handleInputChange('recipientFloorNumber', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="1, 2, Ground, etc."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recipient Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email
                </label>
                <input
                  type="email"
                  value={formData.recipientEmail}
                  onChange={(e) => handleInputChange('recipientEmail', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                {errors.recipientEmail && (
                  <p className="mt-1 text-sm text-red-600">{errors.recipientEmail}</p>
                )}
              </div>

              {/* Recipient Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Phone
                </label>
                <input
                  type="tel"
                  value={formData.recipientPhone}
                  onChange={(e) => handleInputChange('recipientPhone', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Recipient Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipient Address
              </label>
              <textarea
                value={formData.recipientAddress}
                onChange={(e) => handleInputChange('recipientAddress', e.target.value)}
                rows={2}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="Recipient's full address"
              />
            </div>
          </div>
        </div>

        {/* Delivery Status */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Delivery Status</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Is Delivered */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isDelivered}
                    onChange={(e) => handleInputChange('isDelivered', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Mark as Delivered</span>
                </label>
                {formData.isDelivered && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Delivery Date *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.deliveryDate}
                      onChange={(e) => handleInputChange('deliveryDate', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    {errors.deliveryDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.deliveryDate}</p>
                    )}
                  </div>
                )}
                {errors.isDelivered && (
                  <p className="mt-1 text-sm text-red-600">{errors.isDelivered}</p>
                )}
              </div>

              {/* Is Collected */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isCollected}
                    onChange={(e) => handleInputChange('isCollected', e.target.checked)}
                    disabled={!formData.isDelivered}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Mark as Collected</span>
                </label>
                {formData.isCollected && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Collection Date *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.collectedDate}
                      onChange={(e) => handleInputChange('collectedDate', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    {errors.collectedDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.collectedDate}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Notification Settings</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notification Methods
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.notificationMethods.includes(1)}
                    onChange={(e) => handleNotificationMethodChange(1, e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <MailIcon className="ml-2 mr-1 w-4 h-4" />
                  <span className="text-sm text-gray-700">Email</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.notificationMethods.includes(2)}
                    onChange={(e) => handleNotificationMethodChange(2, e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <Phone className="ml-2 mr-1 w-4 h-4" />
                  <span className="text-sm text-gray-700">SMS</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.notificationMethods.includes(3)}
                    onChange={(e) => handleNotificationMethodChange(3, e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <Bell className="ml-2 mr-1 w-4 h-4" />
                  <span className="text-sm text-gray-700">Push Notification</span>
                </label>
              </div>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.notifyRecipient}
                  onChange={(e) => handleInputChange('notifyRecipient', e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Notify recipient of changes</span>
              </label>
            </div>
          </div>
        </div>

        {/* Update Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Update Information</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Update Reason
              </label>
              <input
                type="text"
                value={formData.updateReason}
                onChange={(e) => handleInputChange('updateReason', e.target.value)}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="Reason for this update (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admin Notes
              </label>
              <textarea
                value={formData.adminNotes}
                onChange={(e) => handleInputChange('adminNotes', e.target.value)}
                rows={3}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="Internal notes about this delivery (optional)"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.forceUpdate}
                    onChange={(e) => handleInputChange('forceUpdate', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Force update</span>
                </label>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.skipValidation}
                    onChange={(e) => handleInputChange('skipValidation', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Skip validation</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(`/deliveries/${deliveryId}`)}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default EditDelivery